/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: FAQ Section for Video Translator landing page
 */
import Link from 'next/link';
import React, { useState } from 'react';

import styles from './index.module.scss';

/**
 * 解析文本中的 Markdown 链接语法 [text](url) 并转换为 React 元素（FAQ 答案 / 列表项复用）
 */
export const parseFaqInlineMarkdownLinks = (text: string): React.ReactNode[] => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // 添加链接前的文本
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // 添加链接
    const [, linkText, url] = match;
    const isExternal = url.startsWith('http');
    parts.push(
      isExternal ? (
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {linkText}
        </a>
      ) : (
        <Link key={match.index} href={url} className={styles.link}>
          {linkText}
        </Link>
      ),
    );
    lastIndex = match.index + match[0].length;
  }

  // 添加剩余文本
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/** 列表项配置 */
export interface FAQListItem {
  /** 列表项标题（加粗显示） */
  title?: string;
  /** 列表项内容（支持 [text](url) 链接语法） */
  content: string;
}

export interface FAQItem {
  question: string;
  /** 纯文本答案 */
  answer?: string;
  /** 有序列表（ol） */
  orderedList?: FAQListItem[];
  /** 无序列表（ul） */
  unorderedList?: FAQListItem[];
  /** 列表前的说明文本 */
  listIntro?: string;
}

export interface FAQSectionProps {
  /** 区块标题 */
  title: string;
  /** FAQ 列表 */
  faqs: FAQItem[];
}

interface FAQItemComponentProps {
  item: FAQItem;
  index: number;
  isExpanded: boolean;
  onClick: () => void;
}

// 加号图标
const PlusIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </svg>
);

// 减号图标
const MinusIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const FAQItemComponent: React.FC<FAQItemComponentProps> = ({
  item,
  isExpanded,
  onClick,
}) => {
  // 渲染答案内容
  const renderAnswer = () => {
    // 纯文本答案（支持链接语法）
    if (item.answer) {
      return <p className={styles.answer}>{parseFaqInlineMarkdownLinks(item.answer)}</p>;
    }

    // 列表答案
    const listItems = item.orderedList || item.unorderedList;
    if (listItems) {
      const ListTag = item.orderedList ? 'ol' : 'ul';
      return (
        <>
          {item.listIntro && (
            <p className={styles.listIntro}>
              {parseFaqInlineMarkdownLinks(item.listIntro)}
            </p>
          )}
          <ListTag className={styles.answerList}>
            {listItems.map((listItem, idx) => (
              <li key={idx} className={styles.listItem}>
                {listItem.title && <strong>{listItem.title}</strong>}
                {listItem.title ? ' ' : ''}
                {parseFaqInlineMarkdownLinks(listItem.content)}
              </li>
            ))}
          </ListTag>
        </>
      );
    }

    return null;
  };

  return (
    <div
      className={`${styles.faqItem} ${isExpanded ? styles.expanded : ''}`}
      onClick={onClick}
    >
      <div className={styles.faqContent}>
        {/* 问题行 */}
        <div className={styles.questionRow}>
          <h3 className={styles.question}>{item.question}</h3>
          <div className={styles.iconWrapper}>
            {isExpanded ? <MinusIcon /> : <PlusIcon />}
          </div>
        </div>

        {/* 答案 */}
        <div className={`${styles.answerWrapper} ${isExpanded ? styles.show : ''}`}>
          {renderAnswer()}
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC<FAQSectionProps> = ({ title, faqs }) => {
  // 默认展开第一个
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set([0]));

  const handleItemClick = (index: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题 */}
        <h2 className={styles.title}>{title}</h2>

        {/* FAQ 列表 */}
        <div className={styles.faqList}>
          {faqs.map((item, index) => (
            <FAQItemComponent
              key={index}
              item={item}
              index={index}
              isExpanded={expandedIds.has(index)}
              onClick={() => handleItemClick(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
